import React, { useState, useEffect, useCallback, useRef } from 'react'
import Footer from '../components/footer'
import Navbar from '../components/navbar'
import Button from '../components/input-components/button'
import TextField from '../components/input-components/textfield'
import TextArea from '../components/input-components/textarea'
import Checkbox from '../components/input-components/checkbox'
import donationsApi, { DonationType } from '../services/donations-api'

// Curated SF Symbol icons suitable for donation types
// These render natively on iOS; the admin UI shows emoji approximations
const SF_SYMBOL_ICONS: { name: string; label: string; emoji: string }[] = [
  { name: '', label: 'No icon', emoji: '' },
  { name: 'banknote', label: 'Zakat / Money', emoji: '💵' },
  { name: 'hands.sparkles.fill', label: 'Dua / Giving', emoji: '🤲' },
  { name: 'moon.stars.fill', label: 'Ramadan / Eid', emoji: '🌙' },
  { name: 'building.2.fill', label: 'Masjid / Building', emoji: '🏛️' },
  { name: 'fork.knife', label: 'Fitrana / Food', emoji: '🍽️' },
  { name: 'gift.fill', label: 'Sadaqah / Gift', emoji: '🎁' },
  { name: 'book.fill', label: 'Quran / Education', emoji: '📖' },
  { name: 'graduationcap.fill', label: 'Scholarship / Education', emoji: '🎓' },
  { name: 'house.fill', label: 'Shelter / Housing', emoji: '🏠' },
  { name: 'person.2.fill', label: 'Community / Family', emoji: '👥' },
  { name: 'drop.fill', label: 'Water / Relief', emoji: '💧' },
  { name: 'leaf.fill', label: 'Sustainability', emoji: '🍃' },
  { name: 'globe.europe.africa.fill', label: 'International Aid', emoji: '🌍' },
  { name: 'star.fill', label: 'Jummah / Featured', emoji: '⭐' },
  { name: 'hand.raised.fill', label: 'Pledge / Commitment', emoji: '✋' },
]

interface DonationTypeFormProps {
  donationType: DonationType | null
  onSave: (donationType: DonationType) => void
  onCancel: () => void
  isSaving?: boolean
}

const DonationTypeForm: React.FC<DonationTypeFormProps> = ({ donationType, onSave, onCancel, isSaving = false }) => {
  const [name, setName] = useState(donationType?.name || '')
  const [description, setDescription] = useState(donationType?.description || '')
  // Amounts are stored in pence, display in pounds
  const [suggestedAmountsStr, setSuggestedAmountsStr] = useState(
    donationType?.suggestedAmounts.map(a => a / 100).join(', ') || ''
  )
  const [minimumAmount, setMinimumAmount] = useState(
    donationType ? (donationType.minimumAmount / 100).toString() : '1'
  )
  const [iconName, setIconName] = useState(donationType?.iconName || '')
  const [allowCustomAmount, setAllowCustomAmount] = useState(
    donationType?.allowCustomAmount ?? true
  )
  const [allowRecurring, setAllowRecurring] = useState(donationType?.allowRecurring ?? false)
  const [recurringFrequencies, setRecurringFrequencies] = useState<string[]>(
    donationType?.recurringFrequencies ?? []
  )
  const [isActive, setIsActive] = useState(donationType?.isActive ?? true)

  const handleSubmit = () => {
    // Convert pounds to pence for storage
    const suggestedAmounts = suggestedAmountsStr
      .split(',')
      .map(s => Math.round(parseFloat(s.trim()) * 100))
      .filter(n => !isNaN(n))

    const newDonationType: DonationType = {
      id: donationType?.id || name.toLowerCase().replace(/\s+/g, '-'),
      name,
      description,
      iconName,
      suggestedAmounts,
      minimumAmount: Math.round((parseFloat(minimumAmount) || 1) * 100),
      allowCustomAmount,
      allowRecurring,
      recurringFrequencies: allowRecurring ? recurringFrequencies : [],
      isActive,
      order: donationType?.order ?? -1, // -1 signals "assign next order" to parent
    }

    onSave(newDonationType)
  }

  const isValid = name.trim() !== '' && suggestedAmountsStr.trim() !== ''

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        {donationType ? 'Edit Donation Type' : 'Add New Donation Type'}
      </h3>
      <div className="space-y-4">
        <TextField
          id="name"
          type="text"
          label="Name"
          placeholder="e.g. Zakat, Fitrana, Sadaqah"
          value={name}
          setValue={setName}
        />

        <TextArea
          id="description"
          type="text"
          label="Description"
          placeholder="Brief description shown to donors"
          value={description}
          setValue={setDescription}
        />

        <div>
          <label htmlFor="iconName" className="block text-sm font-medium text-gray-700 mb-1">
            Icon (optional)
          </label>
          <div className="flex items-center gap-3">
            <select
              id="iconName"
              value={iconName}
              onChange={(e) => setIconName(e.target.value)}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              {SF_SYMBOL_ICONS.map((icon) => (
                <option key={icon.name} value={icon.name}>
                  {icon.emoji ? `${icon.emoji} ${icon.label}` : icon.label}
                </option>
              ))}
            </select>
            {iconName && (
              <span className="text-2xl" title={iconName}>
                {SF_SYMBOL_ICONS.find(i => i.name === iconName)?.emoji || ''}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Displays as an SF Symbol icon on iOS. Leave empty for no icon.
          </p>
        </div>

        <TextField
          id="suggestedAmounts"
          type="text"
          label="Suggested Amounts (comma separated)"
          placeholder="e.g. 10, 25, 50, 100"
          value={suggestedAmountsStr}
          setValue={setSuggestedAmountsStr}
        />

        <TextField
          id="minimumAmount"
          type="number"
          label="Minimum Amount (£)"
          placeholder="1"
          value={minimumAmount}
          setValue={setMinimumAmount}
        />

        <div className="bg-gray-50 rounded-lg p-4 space-y-3 border border-gray-200">
          <Checkbox
            id="allowCustomAmount"
            label="Allow custom amount entry"
            checked={allowCustomAmount}
            onChange={setAllowCustomAmount}
          />
          <Checkbox
            id="isActive"
            label="Active (visible to donors)"
            checked={isActive}
            onChange={setIsActive}
          />
        </div>

        <div className="bg-gray-50 rounded-lg p-4 space-y-3 border border-gray-200">
          <Checkbox
            id="allowRecurring"
            label="Allow recurring donations"
            checked={allowRecurring}
            onChange={setAllowRecurring}
          />
          {allowRecurring && (
            <div className="ml-6 space-y-2">
              <p className="text-sm font-medium text-gray-700">Frequencies:</p>
              <div className="flex gap-4">
                {(['weekly', 'monthly', 'yearly'] as const).map((freq) => (
                  <label key={freq} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={recurringFrequencies.includes(freq)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRecurringFrequencies([...recurringFrequencies, freq])
                        } else {
                          setRecurringFrequencies(recurringFrequencies.filter(f => f !== freq))
                        }
                      }}
                      className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                    />
                    {freq.charAt(0).toUpperCase() + freq.slice(1)}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            id="saveDonationType"
            label={donationType ? 'Update' : 'Add Donation Type'}
            isLoading={isSaving}
            onClick={handleSubmit}
            disabled={!isValid || isSaving}
          />
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

interface DonationTypeCardProps {
  donationType: DonationType
  onEdit: () => void
  onDelete: () => void
  onToggleActive: () => void
  onDragStart: (e: React.DragEvent) => void
  onDragOver: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent) => void
  onDragEnd: () => void
  onTouchStart: (e: React.TouchEvent) => void
  isDragging: boolean
  isDragOver: boolean
}

const DonationTypeCard: React.FC<DonationTypeCardProps> = ({
  donationType,
  onEdit,
  onDelete,
  onToggleActive,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onTouchStart,
  isDragging,
  isDragOver,
}) => {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      data-donation-id={donationType.id}
      className={`bg-white rounded-lg border p-4 transition-all ${
        donationType.isActive ? 'border-gray-200' : 'border-gray-200 opacity-60'
      } ${isDragging ? 'opacity-50 scale-[0.98]' : ''} ${
        isDragOver ? 'border-teal-400 border-2 bg-teal-50' : ''
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          {/* Drag Handle */}
          <div
            className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 pt-1 select-none"
            style={{ touchAction: 'none' }}
            onTouchStart={onTouchStart}
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
            </svg>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              {donationType.iconName && (
                <span className="text-lg" title={donationType.iconName}>
                  {SF_SYMBOL_ICONS.find(i => i.name === donationType.iconName)?.emoji || ''}
                </span>
              )}
              <h4 className="font-semibold text-gray-800">{donationType.name}</h4>
            {!donationType.isActive && (
              <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                Inactive
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-1">{donationType.description}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {donationType.suggestedAmounts.map((amount) => (
              <span
                key={amount}
                className="text-xs bg-teal-50 text-teal-700 px-2 py-1 rounded"
              >
                £{(amount / 100).toFixed(2)}
              </span>
            ))}
            {donationType.allowCustomAmount && (
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                + Custom
              </span>
            )}
          </div>
            <p className="text-xs text-gray-500 mt-2">
              Min: £{(donationType.minimumAmount / 100).toFixed(2)}
              {donationType.allowRecurring && donationType.recurringFrequencies.length > 0 && (
                <span className="ml-2 text-teal-600">
                  Recurring: {donationType.recurringFrequencies.join(', ')}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-2">
          <button
            onClick={onToggleActive}
            className={`p-2 rounded hover:bg-gray-100 ${donationType.isActive ? 'text-green-600' : 'text-gray-400'}`}
            title={donationType.isActive ? 'Deactivate' : 'Activate'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {donationType.isActive ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              )}
            </svg>
          </button>
          <button
            onClick={onEdit}
            className="p-2 rounded hover:bg-gray-100 text-gray-600"
            title="Edit"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={onDelete}
            className="p-2 rounded hover:bg-gray-100 text-red-600"
            title="Delete"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}

const DonationsPage: React.FC = () => {
  const [donationTypes, setDonationTypes] = useState<DonationType[]>([])
  const [editingType, setEditingType] = useState<DonationType | null>(null)
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [draggedId, setDraggedId] = useState<string | null>(null)
  const [dragOverId, setDragOverId] = useState<string | null>(null)

  // Fetch donation types on mount
  useEffect(() => {
    const fetchDonationTypes = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const types = await donationsApi.listDonationTypes()
        setDonationTypes(types)
      } catch (err) {
        console.error('Failed to fetch donation types:', err)
        setError('Failed to load donation types. Please try again.')
      } finally {
        setIsLoading(false)
      }
    }
    fetchDonationTypes()
  }, [])

  const handleSave = async (donationType: DonationType) => {
    try {
      setIsSaving(true)
      setError(null)

      if (editingType) {
        // Update existing
        const updated = await donationsApi.updateDonationType(donationType.id, donationType)
        setDonationTypes(prev =>
          prev.map(dt => (dt.id === updated.id ? updated : dt))
        )
      } else {
        // Create new - assign next order number
        const maxOrder = donationTypes.reduce((max, dt) => Math.max(max, dt.order), 0)
        const newDonationTypeWithOrder = { ...donationType, order: maxOrder + 1 }
        const created = await donationsApi.createDonationType(newDonationTypeWithOrder)
        setDonationTypes(prev => [...prev, created])
      }
      setEditingType(null)
      setIsAddingNew(false)
    } catch (err) {
      console.error('Failed to save donation type:', err)
      setError('Failed to save donation type. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this donation type?')) {
      try {
        setError(null)
        setInfo(null)
        await donationsApi.deleteDonationType(id)
        setDonationTypes(prev => prev.filter(dt => dt.id !== id))
      } catch (err: any) {
        console.error('Failed to delete donation type:', err)
        const apiError = err?.response?.data?.error || err?.response?.data?.message || err?.response?.error || err?.message || ''
        if (apiError.includes('linked to a campaign')) {
          // Auto-hide the donation type instead of deleting
          try {
            const updated = await donationsApi.updateDonationType(id, { isActive: false })
            setDonationTypes(prev => prev.map(dt => (dt.id === id ? updated : dt)))
            const campaignMatch = apiError.match(/linked to campaign '(.+)'/)
            const campaignName = campaignMatch ? campaignMatch[1] : 'a campaign'
            setInfo(`This donation type is linked to the "${campaignName}" campaign and cannot be deleted. It has been hidden instead to preserve campaign records.`)
          } catch {
            setError('Failed to hide donation type. Please try again.')
          }
        } else {
          setError('Failed to delete donation type. Please try again.')
        }
      }
    }
  }

  const handleToggleActive = async (id: string) => {
    const donationType = donationTypes.find(dt => dt.id === id)
    if (!donationType) return

    try {
      setError(null)
      const updated = await donationsApi.updateDonationType(id, {
        isActive: !donationType.isActive
      })
      setDonationTypes(prev =>
        prev.map(dt => (dt.id === id ? updated : dt))
      )
    } catch (err) {
      console.error('Failed to toggle donation type status:', err)
      setError('Failed to update donation type. Please try again.')
    }
  }

  const handleCancel = () => {
    setEditingType(null)
    setIsAddingNew(false)
  }

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedId(id)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent, id: string) => {
    e.preventDefault()
    if (draggedId !== id) {
      setDragOverId(id)
    }
  }

  const handleDrop = async (e: React.DragEvent, targetId: string) => {
    e.preventDefault()
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null)
      setDragOverId(null)
      return
    }

    // Get sorted list
    const sortedTypes = [...donationTypes].sort((a, b) => a.order - b.order)
    const draggedIndex = sortedTypes.findIndex(dt => dt.id === draggedId)
    const targetIndex = sortedTypes.findIndex(dt => dt.id === targetId)

    if (draggedIndex === -1 || targetIndex === -1) return

    // Reorder the array
    const [draggedItem] = sortedTypes.splice(draggedIndex, 1)
    sortedTypes.splice(targetIndex, 0, draggedItem)

    // Update order values
    const updatedTypes = sortedTypes.map((dt, index) => ({
      ...dt,
      order: index + 1
    }))

    // Optimistically update UI
    setDonationTypes(updatedTypes)
    setDraggedId(null)
    setDragOverId(null)

    // Save the new orders to the API
    try {
      setError(null)
      const updatePromises = updatedTypes
        .filter((dt, index) => {
          const original = donationTypes.find(o => o.id === dt.id)
          return original && original.order !== dt.order
        })
        .map(dt => donationsApi.updateDonationType(dt.id, { order: dt.order }))

      await Promise.all(updatePromises)
    } catch (err) {
      console.error('Failed to save new order:', err)
      setError('Failed to save new order. Please try again.')
      // Revert on error
      const types = await donationsApi.listDonationTypes()
      setDonationTypes(types)
    }
  }

  const handleDragEnd = () => {
    setDraggedId(null)
    setDragOverId(null)
  }

  // Touch drag support for mobile
  const touchDragId = useRef<string | null>(null)

  const handleTouchStart = useCallback((id: string, e: React.TouchEvent) => {
    e.preventDefault()
    touchDragId.current = id
    setDraggedId(id)

    const handleTouchMove = (ev: TouchEvent) => {
      ev.preventDefault()
      const touch = ev.touches[0]
      const el = document.elementFromPoint(touch.clientX, touch.clientY)
      const card = el?.closest('[data-donation-id]') as HTMLElement | null
      const overId = card?.dataset.donationId || null
      if (overId && overId !== touchDragId.current) {
        setDragOverId(overId)
      }
    }

    const handleTouchEnd = (ev: TouchEvent) => {
      const touch = ev.changedTouches[0]
      const el = document.elementFromPoint(touch.clientX, touch.clientY)
      const card = el?.closest('[data-donation-id]') as HTMLElement | null
      const targetId = card?.dataset.donationId

      if (targetId && touchDragId.current && targetId !== touchDragId.current) {
        // Reuse the drop logic directly
        const sorted = [...donationTypes].sort((a, b) => a.order - b.order)
        const dragIdx = sorted.findIndex(dt => dt.id === touchDragId.current)
        const targetIdx = sorted.findIndex(dt => dt.id === targetId)
        if (dragIdx !== -1 && targetIdx !== -1) {
          const [item] = sorted.splice(dragIdx, 1)
          sorted.splice(targetIdx, 0, item)
          const updated = sorted.map((dt, i) => ({ ...dt, order: i + 1 }))
          setDonationTypes(updated)
          // Save to API
          const saveUpdates = async () => {
            try {
              setError(null)
              const promises = updated
                .filter(dt => {
                  const orig = donationTypes.find(o => o.id === dt.id)
                  return orig && orig.order !== dt.order
                })
                .map(dt => donationsApi.updateDonationType(dt.id, { order: dt.order }))
              await Promise.all(promises)
            } catch {
              setError('Failed to save new order. Please try again.')
              const types = await donationsApi.listDonationTypes()
              setDonationTypes(types)
            }
          }
          saveUpdates()
        }
      }

      touchDragId.current = null
      setDraggedId(null)
      setDragOverId(null)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
    }

    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    document.addEventListener('touchend', handleTouchEnd)
  }, [donationTypes])

  const showForm = isAddingNew || editingType !== null

  return (
    <div className="flex flex-col h-screen">
      <Navbar />
      <div className="md:container mx-auto flex-1 py-8 px-4 overflow-auto">
        <div className="w-full max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-xl font-bold text-gray-800">Donation Types</h1>
              <p className="text-gray-600 text-sm mt-1">
                Configure the donation categories shown in the app
              </p>
            </div>
            {!showForm && (
              <button
                onClick={() => setIsAddingNew(true)}
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Type
              </button>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Info Message */}
          {info && (
            <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg">
              {info}
            </div>
          )}

          {/* Form */}
          {showForm && (
            <div className="mb-6">
              <DonationTypeForm
                donationType={editingType}
                onSave={handleSave}
                onCancel={handleCancel}
                isSaving={isSaving}
              />
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-teal-600 border-t-transparent"></div>
              <p className="text-gray-500 mt-3">Loading donation types...</p>
            </div>
          )}

          {/* List */}
          {!isLoading && (
            <div className="space-y-3">
              {donationTypes
                .sort((a, b) => a.order - b.order)
                .map(dt => (
                  <DonationTypeCard
                    key={dt.id}
                    donationType={dt}
                    onEdit={() => setEditingType(dt)}
                    onDelete={() => handleDelete(dt.id)}
                    onToggleActive={() => handleToggleActive(dt.id)}
                    onDragStart={(e) => handleDragStart(e, dt.id)}
                    onDragOver={(e) => handleDragOver(e, dt.id)}
                    onDrop={(e) => handleDrop(e, dt.id)}
                    onDragEnd={handleDragEnd}
                    onTouchStart={(e) => handleTouchStart(dt.id, e)}
                    isDragging={draggedId === dt.id}
                    isDragOver={dragOverId === dt.id}
                  />
                ))}
            </div>
          )}

          {!isLoading && donationTypes.length === 0 && !showForm && (
            <div className="text-center py-12 text-gray-500">
              No donation types configured. Click "Add Type" to create one.
            </div>
          )}

        </div>
      </div>
      <Footer />
    </div>
  )
}

export default DonationsPage
